<!-- src/lib/components/StatusIcon.svelte -->
<!--
  Linear-inspired status glyph. Picks the right SVG based on a status
  string (column name) so the board's columns get a proper visual
  hierarchy instead of a colored dot.

  Recognised statuses (case-insensitive):
    backlog / triage      → dashed empty circle
    todo / unstarted      → solid empty circle
    started / in progress → half-filled circle (3/4 arc)
    review / in review    → 3/4 filled circle + check stub
    done / completed      → filled check-circle
    canceled / cancelled  → X-circle

  Anything else falls back to an empty circle. Each status has a
  canonical OKLCH color from the living-UI palette.
-->
<script lang="ts">
  interface Props {
    /** Column / status name — case-insensitive fuzzy match. */
    status: string;
    /** Icon size in px. Default 14 — fits inline with card title. */
    size?: number;
  }
  let { status, size = 14 }: Props = $props();

  type Kind = 'backlog' | 'todo' | 'started' | 'review' | 'done' | 'canceled';

  // Map a free-form status string to a canonical Kind. The map is
  // intentionally lenient so existing boards with "Todo / In Progress /
  // Done" still get the right icon without a schema migration.
  function classify(s: string): Kind {
    const lower = s.trim().toLowerCase();
    if (/(cancel|won't|wontfix|abandoned)/.test(lower)) return 'canceled';
    if (/(backlog|triage|icebox)/.test(lower))          return 'backlog';
    if (/(review|qa|testing|verify)/.test(lower))       return 'review';
    if (/(progress|doing|wip|started|active)/.test(lower)) return 'started';
    if (/(done|complete|closed|resolved|shipped)/.test(lower)) return 'done';
    return 'todo';
  }

  const kind = $derived(classify(status));

  // Canonical colors per status — matches the living-UI palette.
  const COLORS: Record<Kind, string> = {
    backlog:  'oklch(0.55 0.03 275 / 0.55)',   // muted
    todo:     'oklch(0.72 0.04 310 / 0.75)',   // neutral
    started:  'oklch(0.70 0.18 85)',           // gold (actively working)
    review:   'oklch(0.68 0.18 265)',          // blue
    done:     'oklch(0.78 0.16 150)',          // green
    canceled: 'oklch(0.55 0.05 275 / 0.45)',   // faded grey
  };
</script>

<span class="status-icon" aria-label={status} title={status} style="--icon-size: {size}px; --icon-color: {COLORS[kind]};">
  {#if kind === 'backlog'}
    <!-- Dashed circle -->
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.4"
        stroke-dasharray="2 2"/>
    </svg>
  {:else if kind === 'todo'}
    <!-- Empty circle -->
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.4"/>
    </svg>
  {:else if kind === 'started'}
    <!-- 3/4 filled pie — "In Progress" -->
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.4"/>
      <!-- 270° arc = 3/4 of a full turn. Start at 12 o'clock,
           sweep clockwise. -->
      <path d="M 7 1.5 A 5.5 5.5 0 1 1 1.5 7 L 7 7 Z" fill="currentColor"/>
    </svg>
  {:else if kind === 'review'}
    <!-- Circle with a checkmark stub -->
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.4"/>
      <path d="M4.5 7.2 L6.3 9 L9.8 5.2" stroke="currentColor" stroke-width="1.4"
        stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  {:else if kind === 'done'}
    <!-- Filled circle + white check -->
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="6" fill="currentColor"/>
      <path d="M4 7.2 L6 9 L10 5" stroke="oklch(0.08 0.02 275)" stroke-width="1.6"
        stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  {:else if kind === 'canceled'}
    <!-- Circle with an X -->
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.4"/>
      <path d="M5 5 L9 9 M9 5 L5 9" stroke="currentColor" stroke-width="1.4"
        stroke-linecap="round"/>
    </svg>
  {/if}
</span>

<style>
  .status-icon {
    display: inline-flex;
    width: var(--icon-size);
    height: var(--icon-size);
    color: var(--icon-color);
    flex-shrink: 0;
    vertical-align: middle;
  }
</style>
